import React, { useState } from 'react'
import { Link } from 'react-router-dom';
import Select from 'react-select';
import DatePicker from 'react-multi-date-picker';
import toast from 'react-hot-toast';
import withReactContent from 'sweetalert2-react-content';
import Swal from 'sweetalert2';
import ContentLoader from '../loader/ContentLoader';
import { commonService } from '../../services/common.service';
import { walletTopupService } from '../../services/wallet-topup.service';
import { SOMETHING_WENT_WRONG } from '../../constants/strings';

const GRAD = "linear-gradient(135deg, #667eea 0%, #764ba2 100%)";

const REQUEST_TYPE_OPTIONS = [
  { value: "topup", label: "Top-up" },
];

const PAYMENT_MODE_OPTIONS = [
  { value: "neft", label: "NEFT" },
  { value: "rtgs", label: "RTGS" },
  { value: "imps", label: "IMPS" },
  { value: "upi", label: "UPI" },
  { value: "cheque", label: "Cheque" },
  { value: "cash", label: "Cash" },
];

const initialTopup = {
  request_type: "topup",
  amount: "",
  payment_mode: "",
  payment_ref: "",
  payment_date: "",
  remarks: "",
};

const WalletTopup = () => {
  const [loading, setLoading] = useState(false);
  const [topup, setTopup] = useState(initialTopup);
  const [proofFile, setProofFile] = useState(null);
  const [fileInputKey, setFileInputKey] = useState(0);

  const showSwal = (title, text, icon) => {
    withReactContent(Swal).fire({ title, text, icon })
  }

  const handleChange = (e) => {
    setTopup({ ...topup, [e.target.name]: e.target.value });
  }

  const handleProofChange = (e) => {
    const file = e.target.files?.[0] || null;
    if (file && !(file.type.startsWith("image/") || file.type === "application/pdf")) {
      toast.error("Only image or PDF files are allowed");
      e.target.value = "";
      setProofFile(null);
      return;
    }
    setProofFile(file);
  }

  const validate = () => {
    if (!topup.request_type) return "Request type is required";
    if (!(Number(topup.amount) > 0)) return "Enter a valid amount";
    if (!topup.payment_mode) return "Payment mode is required";
    if (!topup.payment_ref.trim()) return "Payment reference is required";
    if (!topup.payment_date) return "Payment date is required";
    if (!proofFile) return "Payment proof is required";
    return null;
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    const err = validate();
    if (err) {
      toast.error(err);
      return;
    }
    const confirm = await withReactContent(Swal).fire({
      title: "Submit top-up request?",
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Yes, submit",
      cancelButtonText: "Cancel"
    });
    if (!confirm.isConfirmed) return;

    try {
      setLoading(true);
      const uploadRes = await commonService.uploadFile(proofFile);
      const proofPath = uploadRes?.filesUploaded?.[0]?.path;
      if (!uploadRes?.success || !proofPath) {
        toast.error(uploadRes?.message || "Payment proof upload failed");
        return;
      }
      const res = await walletTopupService.create({
        ...topup,
        amount: Number(topup.amount),
        payment_ref: topup.payment_ref.trim(),
        payment_proof: proofPath,
        remarks: topup.remarks.trim(),
      });
      if (res?.success) {
        await withReactContent(Swal).fire({ title: "Success", text: res.message, icon: "success" });
        setTopup(initialTopup);
        setProofFile(null);
        setFileInputKey((k) => k + 1);
      } else {
        toast.error(res?.message);
      }
    } catch (err) {
      showSwal("Error", SOMETHING_WENT_WRONG, "error")
    } finally {
      setLoading(false);
    }
  }

  if (loading) return (<ContentLoader loaderType="circleSpin" />)

  const labelStyle = { fontSize: 11, letterSpacing: 0.5, textTransform: "uppercase", color: "#6c757d", marginBottom: 6 };
  const inputStyle = { borderRadius: 8, border: "1px solid #dfe3ec", fontSize: 13, height: 40, background: "#fff" };
  const selectStyles = {
    control: (base, state) => ({
      ...base, borderRadius: 8, border: state.isFocused ? "1px solid #667eea" : "1px solid #dfe3ec", minHeight: 40,
      fontSize: 13, boxShadow: state.isFocused ? "0 0 0 3px rgba(102,126,234,.15)" : "none", "&:hover": { borderColor: "#667eea" }
    }),
    option: (base, state) => ({
      ...base, fontSize: 13,
      background: state.isSelected ? "#667eea" : state.isFocused ? "#eef0ff" : "#fff",
      color: state.isSelected ? "#fff" : "#1f2a44",
    }),
    valueContainer: (base) => ({ ...base, padding: "0 10px" }),
    placeholder: (base) => ({ ...base, color: "#a0a8b8", fontSize: 13 }),
    menuPortal: (base) => ({ ...base, zIndex: 2000 }),
  };
  const sectionCard = { background: "#fff", borderRadius: 12, border: "1px solid #e8ebf7", padding: 20, marginBottom: 16 };
  const sectionTitle = (icon, text) => (
    <h6 className="fw-bold mb-3 d-flex align-items-center gap-2" style={{ fontSize: 14, color: "#4b3f8f" }}>
      <i className={"bi " + icon} style={{ color: "#667eea" }}></i> {text}
    </h6>
  );
  const field = (label, required, node, cls = "col-12 col-md-6 col-lg-4") => (
    <div className={cls}>
      <div style={labelStyle}>{label}{required && <span className="text-danger"> *</span>}</div>
      {node}
    </div>
  );

  return (
    <>
      <section className="section dashboard">
        <div className="d-flex align-items-start justify-content-between flex-wrap gap-2 mb-3">
          <div>
            <h4 className="m-0 fw-bold" style={{ color: "#1a202c", fontSize: 20 }}>Wallet Top-up</h4>
            <nav className="d-flex align-items-center gap-1 mt-1" style={{ fontSize: 12, color: "#8a9ab5" }}>
              <Link to="/" style={{ color: "#8a9ab5" }}><i className="bi bi-house-door"></i></Link>
              <i className="bi bi-chevron-right" style={{ fontSize: 9 }}></i>
              <Link to="/wallet" style={{ color: "#8a9ab5" }}>Wallet</Link>
              <i className="bi bi-chevron-right" style={{ fontSize: 9 }}></i>
              <span className="fw-semibold" style={{ color: "#4a5568" }}>Wallet Top-up</span>
            </nav>
          </div>
          <Link to="/wallet" className="btn btn-sm d-flex align-items-center gap-1 text-white px-3"
            style={{ background: "#4f3df5", borderRadius: 8, height: 34, fontSize: 13, fontWeight: 600 }}>
            <i className="bi bi-wallet2"></i> Wallet
          </Link>
        </div>

        <form onSubmit={handleSubmit}>

          <div style={sectionCard}>
            {sectionTitle("bi-cash-coin", "Top-up Detail")}
            <div className="row g-3">
              {field("Request Type", true,
                <Select
                  options={REQUEST_TYPE_OPTIONS}
                  value={REQUEST_TYPE_OPTIONS.find(o => o.value === topup.request_type) || null}
                  onChange={(o) => setTopup({ ...topup, request_type: o ? o.value : "" })}
                  placeholder="Select request type"
                  menuPortalTarget={document.body}
                  styles={selectStyles}
                />
              )}
              {field("Amount", true,
                <input type="number" min="0" className="form-control" placeholder="Enter amount" name="amount"
                  value={topup.amount} onChange={handleChange} style={inputStyle} />
              )}
              {field("Remarks", false,
                <input type="text" className="form-control" placeholder="Any note for the approver" name="remarks"
                  value={topup.remarks} onChange={handleChange} style={inputStyle} />
              )}
            </div>
          </div>

          <div style={sectionCard}>
            {sectionTitle("bi-credit-card", "Payment Detail")}
            <div className="row g-3">
              {field("Payment Mode", true,
                <Select
                  options={PAYMENT_MODE_OPTIONS}
                  value={PAYMENT_MODE_OPTIONS.find(o => o.value === topup.payment_mode) || null}
                  onChange={(o) => setTopup({ ...topup, payment_mode: o ? o.value : "" })}
                  placeholder="Select payment mode"
                  isClearable
                  menuPortalTarget={document.body}
                  styles={selectStyles}
                />
              )}
              {field("Payment Reference", true,
                <input type="text" className="form-control" placeholder="Transaction / UTR number" name="payment_ref"
                  value={topup.payment_ref} onChange={handleChange} style={inputStyle} />
              )}
              {field("Payment Date", true,
                <DatePicker
                  onOpenPickNewDate={false}
                  value={topup.payment_date || null}
                  onChange={(d) => setTopup({ ...topup, payment_date: d ? d.format("YYYY-MM-DD") : "" })}
                  format="YYYY-MM-DD"
                  placeholder="Select payment date"
                  maxDate={new Date()}
                  editable={false}
                  inputClass="form-control"
                  containerStyle={{ width: "100%" }}
                  style={{ ...inputStyle, width: "100%" }}
                />
              )}
              {field("Payment Proof", true,
                <input key={fileInputKey} type="file" className="form-control" accept="image/*,application/pdf"
                  onChange={handleProofChange} style={{ ...inputStyle, paddingTop: 8 }} />,
                "col-12"
              )}
            </div>
          </div>

          <div className="d-flex justify-content-end gap-2">
            <Link to="/wallet" className="btn px-4 d-flex align-items-center"
              style={{ background: "#fff", border: "1px solid #dfe3ec", borderRadius: 8, height: 40, fontSize: 13, color: "#444" }}>
              Back
            </Link>
            <button type="submit" className="btn px-4 text-white fw-semibold"
              style={{ background: GRAD, border: "none", borderRadius: 8, height: 40, fontSize: 13 }}>
              Submit Request
            </button>
          </div>

        </form>
      </section>
    </>
  );
}

export default WalletTopup;
